
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

// 定义接口函数类型
type TagApiFunc = (tagName: string) => Promise<boolean>;

@Component
export default class TagSelectPopup extends Vue {
  // 弹窗显示隐藏 父组件控制
  @Prop({ default: false }) visible!: boolean
  // 双向绑定选中标签（外部真实数据）
  @Prop({ default: () => [] }) value!: string[]
  // 最大可选标签数量
  @Prop({ default: 5 }) maxSelect!: number
  // 系统基础固定标签
  @Prop({ default: () => ['店家推荐', '新品上市', '爆款返场', '限时秒杀', '粉丝福利', '应季新品'] }) commonTags!: string[]
  // 用户自定义新增标签
  @Prop({ default: () => [] }) customTags!: string[]

  // 【新增】新增标签接口方法，父组件传入
  @Prop() addTagApi?: TagApiFunc
  // 【新增】删除标签接口方法，父组件传入
  @Prop() deleteTagApi?: TagApiFunc

  // 新增弹窗加载状态
  loadingAdd = false
  // 删除弹窗加载状态
  loadingDel = false

  // 新增标签弹窗状态
  showAddPopup = false
  // 新增标签输入值
  newTagName = ''
  // 删除确认弹窗
  showDeletePopup = false
  // 当前待删除标签
  deleteTarget = ''

  // 本地临时选中标签（操作副本，不污染外部value）
  tempSelectedList: string[] = []

  // 合并全部可用标签：系统标签 + 用户自定义标签
  get allCommonTags() {
    return [...this.commonTags, ...this.customTags]
  }

  // 弹窗显隐
  get popupVisible() {
    return this.visible
  }
  set popupVisible(val: boolean) {
    this.$emit('update:visible', val)
  }

  mounted() {
    this.syncTempList()
    // 挂到 body，避免 iOS 上被页面底部栏盖住
    document.body.appendChild(this.$el)
  }

  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  }

  // 监听prop visible变化
  @Watch('visible')
  onVisibleChange(newVal: boolean) {
    if (newVal) {
      this.syncTempList()
      // 清空弹窗残留状态
      this.newTagName = ''
      this.deleteTarget = ''
      this.showAddPopup = false
      this.showDeletePopup = false
    }
  }

  // 同步外部value到临时副本
  syncTempList() {
    this.tempSelectedList = [...this.value]
  }

  // 模板使用本地临时标签
  get selectedList() {
    return this.tempSelectedList
  }

  // 判断标签是否为用户自定义标签
  isCustomTag(tag: string) {
    return this.customTags.includes(tag)
  }

  // 切换标签选中/取消（只改本地临时数组）
  toggleTag(tag: string) {
    const list = [...this.tempSelectedList]
    const idx = list.indexOf(tag)
    if (idx > -1) {
      list.splice(idx, 1)
    } else {
      if (list.length >= this.maxSelect) {
        this.$toast(`最多只能选择${this.maxSelect}个标签`)
        return
      }
      list.push(tag)
    }
    this.tempSelectedList = list
  }

  // 点击已选标签关闭按钮移除
  removeSelectTag(idx: number) {
    const list = [...this.tempSelectedList]
    list.splice(idx, 1)
    this.tempSelectedList = list
  }

  // 打开删除自定义标签弹窗
  openDeleteConfirm(tag: string) {
    this.deleteTarget = tag
    this.showDeletePopup = true
  }

  // 确认删除自定义标签【对接删除接口】
  async confirmDeleteTag() {
    const target = this.deleteTarget
    if (!this.deleteTagApi) {
      this.$toast('未传入删除标签接口')
      return
    }
    this.loadingDel = true
    try {
      const res = await this.deleteTagApi(target)
      if (res) {
        // 本地临时选中同步移除该标签
        const selectIdx = this.tempSelectedList.indexOf(target)
        if (selectIdx > -1) {
          const list = [...this.tempSelectedList]
          list.splice(selectIdx, 1)
          this.tempSelectedList = list
        }
        const newCustom = this.customTags.filter(item => item !== target)
        this.$emit('update:custom-tags', newCustom)
        this.$toast('删除成功')
      } else {
        this.$toast('删除失败')
      }
    } catch (err) {
      this.$toast('接口请求异常')
      console.error('删除标签接口报错：', err)
    } finally {
      this.loadingDel = false
      this.showDeletePopup = false
      this.deleteTarget = ''
    }
  }

  // 确认新增自定义标签【对接新增接口】
  async confirmAddTag() {
    const name = this.newTagName.trim()
    if (!name) {
      this.$toast('请输入标签名称')
      return
    }
    if (this.allCommonTags.includes(name)) {
      this.$toast('该标签已存在')
      return
    }
    if (!this.addTagApi) {
      this.$toast('未传入新增标签接口')
      return
    }
    this.loadingAdd = true
    try {
      const res = await this.addTagApi(name)
      if (res) {
        const newCustom = [...this.customTags, name]
        this.$emit('update:custom-tags', newCustom)
        this.$toast('新增标签成功')
        this.newTagName = ''
        this.showAddPopup = false
      } else {
        this.$toast('新增失败')
      }
    } catch (err) {
      this.$toast('接口请求异常')
      console.error('新增标签接口报错：', err)
    } finally {
      this.loadingAdd = false
    }
  }

  // 关闭弹窗（右上角叉号）——不保存，直接丢弃临时数据
  handleClose() {
    this.popupVisible = false
  }

  // 遮罩层关闭弹窗——不保存
  handleMaskClose() {
    this.popupVisible = false
  }

  // 保存按钮点击：同步临时数据给父组件并关闭弹窗
  handleSave() {
    // 同步本地临时选中到父组件 value
    this.$emit('input', [...this.tempSelectedList])
    this.$emit('save', [...this.tempSelectedList])
    this.popupVisible = false
  }
}
